
import React, { useState, useMemo } from 'react';
import { useTheme, useBrand } from '../context/ColorContext';
import { useQuery } from '@tanstack/react-query';
import { dealerService } from '../services/api';
import { MapPin, Phone, Mail, Globe, Loader2, Store, Building2, Palette } from 'lucide-react';

const Bayiler = () => {
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';
    const [dealerType, setDealerType] = useState('distributor'); // 'distributor' or 'mix'
    const [selectedCity, setSelectedCity] = useState('all');

    // Fetch dealers from API
    const { data: allDealers, isLoading } = useQuery({
        queryKey: ['dealers'],
        queryFn: () => dealerService.getAll()
    });

    // Filter dealers by brand and type
    const dealers = useMemo(() => {
        if (!allDealers) return [];
        return allDealers.filter(d =>
            d.dealerType === dealerType &&
            d.brandId === activeBrand?.id
        );
    }, [allDealers, dealerType, activeBrand]);

    // Get unique cities from dealers (for current type)
    const cities = useMemo(() => {
        if (!dealers) return [];
        const uniqueCities = [...new Set(dealers.map(d => d.city))].filter(Boolean).sort();
        return uniqueCities;
    }, [dealers]);

    // Filter dealers by selected city
    const filteredDealers = useMemo(() => {
        if (!dealers) return [];
        if (selectedCity === 'all') return dealers;
        return dealers.filter(d => d.city === selectedCity);
    }, [dealers, selectedCity]);

    // Reset city filter when changing dealer type
    const handleTypeChange = (type) => {
        setDealerType(type);
        setSelectedCity('all');
    };

    // Count by type (for current brand)
    const brandDealers = useMemo(() => allDealers?.filter(d => d.brandId === activeBrand?.id) || [], [allDealers, activeBrand]);
    const distributorCount = useMemo(() => brandDealers.filter(d => d.dealerType === 'distributor').length, [brandDealers]);
    const mixCount = useMemo(() => brandDealers.filter(d => d.dealerType === 'mix').length, [brandDealers]);

    return (
        <div className={`min-h-screen pt-32 pb-20 px-4 md:px-0 container mx-auto lg:pl-32 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {/* Header */}
            <div className="mb-12">
                <span className="text-sm font-bold tracking-[0.4em] opacity-60 uppercase mb-4 block">Türkiye Geneli Hizmet Ağı</span>
                <h1 className="text-5xl md:text-8xl font-black uppercase tracking-tighter leading-none mb-8">
                    Distribütörler<br />&amp; Bayiler
                </h1>
                <p className="max-w-2xl text-lg font-light opacity-70 leading-relaxed">
                    DÜFA kalitesini Türkiye'nin her noktasına ulaştıran profesyonel dağıtım ağımızla hizmetinizdeyiz.
                </p>
            </div>

            {/* Main Type Tabs */}
            <div className="grid grid-cols-2 gap-2 md:flex md:gap-4 mb-6 md:mb-10">
                <button
                    onClick={() => handleTypeChange('distributor')}
                    className={`flex items-center justify-center gap-2 md:gap-3 px-3 md:px-8 py-3 md:py-5 rounded-2xl md:rounded-3xl font-black uppercase tracking-wider md:tracking-widest text-[10px] md:text-sm transition-all ${dealerType === 'distributor'
                        ? 'bg-gradient-to-r from-[#5AA668] to-[#4a9058] text-white shadow-2xl shadow-[#5AA668]/30 md:scale-105'
                        : isDark
                            ? 'bg-white/5 border border-white/10 hover:bg-white/10'
                            : 'bg-black/5 border border-black/5 hover:bg-black/10'
                        }`}
                >
                    <Building2 size={16} className="md:w-5 md:h-5 shrink-0" />
                    <span className="truncate"><span className="hidden sm:inline">Bölge </span>Distribütörleri</span>
                    <span className={`text-[9px] md:text-xs px-2 md:px-3 py-0.5 md:py-1 rounded-full shrink-0 ${dealerType === 'distributor' ? 'bg-white/20' : isDark ? 'bg-white/10' : 'bg-black/10'}`}>
                        {distributorCount}
                    </span>
                </button>
                <button
                    onClick={() => handleTypeChange('mix')}
                    className={`flex items-center justify-center gap-2 md:gap-3 px-3 md:px-8 py-3 md:py-5 rounded-2xl md:rounded-3xl font-black uppercase tracking-wider md:tracking-widest text-[10px] md:text-sm transition-all ${dealerType === 'mix'
                        ? 'bg-gradient-to-r from-[#5AA668] to-[#4a9058] text-white shadow-2xl shadow-[#5AA668]/30 md:scale-105'
                        : isDark
                            ? 'bg-white/5 border border-white/10 hover:bg-white/10'
                            : 'bg-black/5 border border-black/5 hover:bg-black/10'
                        }`}
                >
                    <Palette size={16} className="md:w-5 md:h-5 shrink-0" />
                    <span className="truncate"><span className="hidden sm:inline">Mix Makineli </span>Bayiler</span>
                    <span className={`text-[9px] md:text-xs px-2 md:px-3 py-0.5 md:py-1 rounded-full shrink-0 ${dealerType === 'mix' ? 'bg-white/20' : isDark ? 'bg-white/10' : 'bg-black/10'}`}>
                        {mixCount}
                    </span>
                </button>
            </div>

            {/* City Selection - Mobile dropdown */}
            <div className="md:hidden mb-10">
                <label className="text-[10px] font-black uppercase tracking-widest opacity-50 mb-2 block">Şehir Seçin</label>
                <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className={`w-full px-5 py-4 rounded-2xl text-sm font-bold uppercase tracking-wider border appearance-none bg-no-repeat bg-[right_1rem_center] bg-[length:1.25rem] ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-black/10 text-slate-900'}`}
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%235AA668' stroke-width='3'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")` }}
                >
                    <option value="all">TÜMÜ ({dealers?.length || 0})</option>
                    {cities.map(city => (
                        <option key={city} value={city}>
                            {city} ({dealers?.filter(d => d.city === city).length})
                        </option>
                    ))}
                </select>
            </div>

            {/* City Selection - Desktop button list */}
            <div className="hidden md:flex flex-wrap gap-3 mb-16">
                <button
                    onClick={() => setSelectedCity('all')}
                    className={`px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${selectedCity === 'all'
                        ? 'bg-[#5AA668] border-transparent text-white shadow-lg'
                        : isDark ? 'border-white/10 hover:bg-white/5' : 'border-black/5 hover:bg-black/5'}`}
                >
                    TÜMÜ ({dealers?.length || 0})
                </button>
                {cities.map(city => (
                    <button
                        key={city}
                        onClick={() => setSelectedCity(city)}
                        className={`px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all ${selectedCity === city
                            ? 'bg-[#5AA668] border-transparent text-white shadow-lg'
                            : isDark ? 'border-white/10 hover:bg-white/5' : 'border-black/5 hover:bg-black/5'}`}
                    >
                        {city} ({dealers?.filter(d => d.city === city).length})
                    </button>
                ))}
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <Loader2 className="animate-spin opacity-60" size={48} />
                    <p className="opacity-60 font-bold uppercase tracking-widest text-xs">Bayiler Yükleniyor...</p>
                </div>
            )}

            {/* Content List */}
            {!isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredDealers.length === 0 ? (
                        <div className="col-span-full flex flex-col items-center justify-center py-20 gap-4 opacity-60">
                            <Store size={64} strokeWidth={1} />
                            <p className="font-bold">Bu kategoride bayi bulunmuyor</p>
                        </div>
                    ) : (
                        filteredDealers.map((dist) => (
                            <div
                                key={dist.id}
                                className={`group p-8 rounded-[3rem] border backdrop-blur-2xl transition-all hover:-translate-y-2 flex flex-col ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white/40 border-black/5 hover:bg-white/60'}`}
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <div className={`p-4 rounded-2xl ${dealerType === 'distributor' ? 'bg-[#5AA668]/10 text-[#5AA668]' : 'bg-[#5AA668]/10 text-[#5AA668]'}`}>
                                        {dealerType === 'distributor' ? <Building2 size={24} /> : <Palette size={24} />}
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[10px] font-black uppercase tracking-[0.3em] opacity-40 block">{dist.city}</span>
                                        {dist.district && (
                                            <span className="text-[9px] font-bold uppercase tracking-wider opacity-30">{dist.district}</span>
                                        )}
                                    </div>
                                </div>

                                <h3 className={`text-lg font-black uppercase tracking-tight mb-4 leading-tight transition-colors line-clamp-2 ${dealerType === 'distributor' ? 'group-hover:text-[#5AA668]' : 'group-hover:text-[#5AA668]'}`}>
                                    {dist.name}
                                </h3>

                                <div className="space-y-3 opacity-70 text-sm font-light leading-relaxed flex-1 overflow-hidden">
                                    {dist.address && (
                                        <div className="flex gap-3">
                                            <MapPin size={16} className="flex-shrink-0 text-[#5AA668]" />
                                            <p className="line-clamp-2 text-xs">{dist.address}</p>
                                        </div>
                                    )}
                                    {dist.phone && (
                                        <div className="flex items-center gap-3">
                                            <Phone size={16} className="flex-shrink-0 text-[#5AA668]" />
                                            <a href={`tel:${dist.phone}`} className="hover:text-[#5AA668] transition-colors text-xs">{dist.phone}</a>
                                        </div>
                                    )}
                                    {dist.email && (
                                        <div className="flex items-center gap-3">
                                            <Mail size={16} className="flex-shrink-0 text-[#5AA668]" />
                                            <a href={`mailto:${dist.email}`} className="break-all hover:text-[#5AA668] transition-colors text-xs truncate">{dist.email}</a>
                                        </div>
                                    )}
                                    {dist.website && (
                                        <div className="flex items-center gap-3">
                                            <Globe size={16} className="flex-shrink-0 text-[#5AA668]" />
                                            <a href={dist.website} target="_blank" rel="noreferrer" className="hover:text-[#5AA668] transition-colors text-xs truncate">{dist.website}</a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Bottom Info */}
            <div className={`mt-20 p-12 rounded-[4rem] border border-dashed flex flex-col md:flex-row items-center justify-between gap-8 ${isDark ? 'border-white/10' : 'border-black/10'}`}>
                <div>
                    <h4 className="text-2xl font-black uppercase mb-2">Siz de Bayimiz Olun</h4>
                    <p className="opacity-60 font-light">Türkiye'nin yükselen boya markası DÜFA ailesine katılmak için başvuru yapabilirsiniz.</p>
                </div>
                <button className={`px-10 py-5 rounded-2xl font-black uppercase tracking-widest transition-all ${isDark ? 'bg-white text-black hover:bg-white/90' : 'bg-black text-white hover:bg-black/90'}`}>
                    BAŞVURU FORMU
                </button>
            </div>
        </div>
    );
};

export default Bayiler;
